/**
 * Healthcare Context Initialization Service
 * Manages the initialization of healthcare context (organization, clinic, patient)
 */

import { analytics } from "@/lib/analytics";
import { setSentryUserContext } from "@/lib/sentry";
import consultationSocketManager from "@/services/consultation-socket-manager";
import { useClinicStore, useUserStore } from "@/stores";
import {
  getHospitalClinic,
  getOrganization,
} from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { Logger } from "@/utils";
import { loadSelectedRolePermissions } from "./rolePermissionService";
import { loadStaffWithRetry, loadUserPermissions, mapStaffInfoToUserProfile } from "./staffService";

export interface InitializationResult {
  success: boolean;
  error?: string;
}

const normalizeAnalyticsString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;

  const normalized = value.trim();
  if (
    !normalized ||
    normalized.toLowerCase() === "undefined" ||
    normalized.toLowerCase() === "null"
  ) {
    return undefined;
  }

  return normalized;
};

const syncTelemetryUserContext = () => {
  const { profile, permissions } = useUserStore.getState();
  const { organizationId, clinicId } = useClinicStore.getState();
  if (!profile) return;

  const analyticsUserId = profile.userId || profile.id || profile.staffId || profile.username;
  if (!analyticsUserId) return;

  const username = normalizeAnalyticsString(profile.username);
  const displayName =
    normalizeAnalyticsString(profile.name) ||
    normalizeAnalyticsString(profile.fullName) ||
    username;
  const email = normalizeAnalyticsString(profile.email);

  analytics.identifyUser(String(analyticsUserId), {
    ...(username ? { username } : {}),
    ...(displayName ? { name: displayName, $name: displayName } : {}),
    ...(email ? { email, $email: email } : {}),
    staff_id: profile.staffId,
    specialty_id: profile.specialtyId,
    location_count: profile.locations?.length || 0,
    permissions_count: permissions.length,
    organization_id: organizationId,
    clinic_id: clinicId,
  });

  setSentryUserContext({
    userId: analyticsUserId,
    username,
    email,
    staffId: profile.staffId,
    role: profile.roles?.[0],
    specialtyId: profile.specialtyId,
    permissionsCount: permissions.length,
    organizationId,
    clinicId,
  });
};

/**
 * Initialize healthcare context after login
 *
 * Reads organization and clinic from user's AdditionalInfo selection
 * and initializes the clinic store for the current session.
 *
 * Note: Patient ID comes from URL params, not from this initialization.
 * If AdditionalInfo is empty (first login), this will silently skip initialization.
 */
export const initializeHealthcareContext = async (): Promise<InitializationResult> => {
  // Refresh the permissions of the role selected at /additional-info on every initialisation.
  // Deliberately not awaited: the v2 route guard renders its loading state off the store's
  // status, and a slow or failing role lookup must never hold up the rest of the bootstrap.
  void loadSelectedRolePermissions();

  // The role may have just changed (this runs right after the /additional-info submit), so
  // republish the handshake payload of any consultation socket already open. They keep their
  // current connection and send the new role on their next handshake.
  const refreshedSockets = consultationSocketManager.refreshAuthPayload();
  if (refreshedSockets > 0) {
    Logger.info("Refreshed consultation socket handshake for the active role", {
      sockets: refreshedSockets,
    });
  }

  const state = useClinicStore.getState();
  const organization = getOrganization();
  const hospitalClinic = getHospitalClinic();
  const hasSelectedHealthcareContext = Boolean(organization?.value && hospitalClinic?.value);
  const hasChangedHealthcareContext =
    hasSelectedHealthcareContext &&
    (state.organizationId !== organization?.value || state.clinicId !== hospitalClinic?.value);
  const needsInit =
    !state.isInitialized || !state.organizationId || !state.clinicId || hasChangedHealthcareContext;
  const currentProfile = useUserStore.getState().profile;
  // If profile is still a bare auth payload (no staffId/fullName or username equals email),
  // refresh staff info even when the clinic store already claims to be initialized.
  const shouldRefreshStaffProfile =
    !currentProfile?.staffId ||
    !currentProfile?.fullName ||
    !currentProfile?.username ||
    currentProfile.username === currentProfile.email;

  const loadAndStoreStaffInfo = async () => {
    try {
      Logger.info("Loading staff info and permissions...");
      const [staffInfo, permissions] = await Promise.all([
        loadStaffWithRetry(),
        loadUserPermissions(),
      ]);

      Logger.info("Staff info loaded:", {
        hasStaffInfo: !!staffInfo,
        permissionsCount: permissions.length,
        userId: staffInfo?.userId,
        fullName: staffInfo?.fullName,
      });

      if (staffInfo) {
        // Map staff info to user profile and update user store
        const userProfileUpdate = mapStaffInfoToUserProfile(staffInfo, permissions);
        const userStore = useUserStore.getState();

        // If profile is null, use setProfile; otherwise use updateProfile
        if (userStore.profile) {
          userStore.updateProfile(userProfileUpdate);
        } else {
          // Create a full profile with required fields
          userStore.setProfile({
            id: userProfileUpdate.id || userProfileUpdate.userId || "",
            username: userProfileUpdate.username || "",
            name: userProfileUpdate.name || "",
            email: userProfileUpdate.email || "",
            ...userProfileUpdate,
          });
        }

        userStore.setPermissions(permissions);
        syncTelemetryUserContext();
      } else {
        Logger.warn(
          "Failed to load staff info. This might indicate permission issues with the selected organization/clinic.",
        );
        // Don't fail the initialization, but user might need to reselect
      }
    } catch (error) {
      Logger.error("Error loading staff info and permissions:", error);
      // Don't fail initialization, continue without complete profile
    }
  };

  if (!needsInit && !shouldRefreshStaffProfile) {
    syncTelemetryUserContext();
    Logger.info("Healthcare context already initialized and profile is up to date");
    return { success: true };
  }

  // Clinic context already set up, but profile needs refresh
  if (!needsInit && shouldRefreshStaffProfile) {
    await loadAndStoreStaffInfo();
    return { success: true };
  }

  try {
    Logger.info("Healthcare context check:", {
      hasOrganization: !!organization,
      hasHospitalClinic: !!hospitalClinic,
      organizationValue: organization?.value,
      clinicValue: hospitalClinic?.value,
    });

    // If AdditionalInfo is not set yet (first login), skip initialization silently
    // User will be redirected to AdditionalInfo page to select organization and clinic
    if (!organization?.value || !hospitalClinic?.value) {
      Logger.info(
        "AdditionalInfo not set yet. Skipping healthcare context initialization. User needs to select organization and clinic first.",
      );
      return { success: true }; // Return success to not block login flow
    }

    // Set IDs from user selection
    state.setOrganizationId(organization.value);
    state.setClinicId(hospitalClinic.value);

    // Set clinic info (no need to create patient here - patient comes from URL params)
    state.setClinicInfo({
      organizationId: organization.value,
      clinicId: hospitalClinic.value,
      clinicName: hospitalClinic.label,
      organizationName: organization.label,
    });

    state.markAsInitialized();
    Logger.info("Healthcare context initialization completed successfully");

    // Load staff info and permissions (non-blocking, errors are logged internally)
    // This also serves as a permission check - if user doesn't have access
    // to the selected organization/clinic, this will fail
    await loadAndStoreStaffInfo();

    return { success: true };
  } catch (error) {
    const errorMsg = `Healthcare context initialization failed: ${error}`;
    Logger.error(errorMsg, error);
    return { success: false, error: errorMsg };
  }
};

/**
 * Check if healthcare context needs initialization
 */
export const needsHealthcareInitialization = (): boolean => {
  const state = useClinicStore.getState();
  return !state.isInitialized || !state.organizationId || !state.clinicId;
};

/**
 * Reset healthcare context
 */
export const resetHealthcareContext = (): void => {
  const state = useClinicStore.getState();
  state.reset();
  Logger.info("Healthcare context reset");
};
